import { z } from 'zod'

import { iconSchema } from '../icon.ts'
import { dataEntrySchema } from './dataEntry.ts'
import { formItemSchema, titleFormItemSchema } from './form.ts'
import { kpiSchema, type ViewUiKpi } from './kpi.ts'
import { kpiPivotSchema, type ViewUiKpiPivot } from './kpiPivot.ts'
import { SheetSchema, type ViewUISheet } from './sheet.ts'

/**
 * form
 */
const formSchema = z.object({
  type: z.literal('Form'),
  label: z.string(),
  get children() {
    return viewUiFormComponentSchema.array()
  },
  nameFieldName: z.string().optional(),
  recordUrlFieldName: z.string().optional(),
  icon: iconSchema.optional(),
  color: z.string().optional(),
})
export type ViewUIForm = z.infer<typeof formSchema>

const childRecordsFormSchema = z.object({
  type: z.literal('ChildRecordsForm'),
  label: z.string(),
  tableNodeName: z.string(),
  get children() {
    return viewUiFormComponentSchema.array()
  },
  nameFieldName: z.string().optional(),
  recordUrlFieldName: z.string().optional(),
  relation: z.enum([`many_to_one`, `one_to_many`, 'one_to_one']).optional(),
  icon: iconSchema.optional(),
  color: z.string().optional(),
})

export type ViewUIChildRecordsForm = z.infer<typeof childRecordsFormSchema>

const formListSchema = z.object({
  type: z.literal('FormList'),
  label: z.string(),
  path: z.string().array(),
  get children() {
    return viewUiFormComponentSchema.array()
  },
})

export type ViewUIFormList = z.infer<typeof formListSchema>

// grid
const colSchema = z.object({
  type: z.literal('Col'),
  id: z.string(),
  span: z.number(),
  get children() {
    return viewUiFormComponentSchema.array()
  },
})

export type ViewUICol = z.infer<typeof colSchema>

const rowSchema = z.object({
  type: z.literal('Row'),
  id: z.string(),
  gutter: z.number().optional(),
  children: colSchema.array(),
})
export type ViewUIRow = z.infer<typeof rowSchema>

/**
 * root
 */
export const viewUiComponentSchema = z.union([
  rowSchema,
  colSchema,
  formItemSchema,
  titleFormItemSchema,
  formListSchema,
  childRecordsFormSchema,
  dataEntrySchema,
  formSchema,
  SheetSchema,
  kpiSchema,
  kpiPivotSchema,
])
export type ViewUIComponent = z.infer<typeof viewUiComponentSchema>

export type ViewUI = ViewUIComponent[]
export const viewUISchema: z.ZodType<ViewUI> = viewUiComponentSchema.array()

const viewUiFormComponentSchema = z.union([
  rowSchema,
  colSchema,
  formItemSchema,
  titleFormItemSchema,
  formListSchema,
  childRecordsFormSchema,
  dataEntrySchema,
])
export type ViewUiFormComponent = z.infer<typeof viewUiFormComponentSchema>

export type ViewUiRootComponent = ViewUIForm | ViewUISheet | ViewUiKpi | ViewUiKpiPivot
